import { Component, computed, inject, signal, WritableSignal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { AuthService } from '@card-software/nexus-utilities';
import {
  DAILY_PRODUCTION_PROCESS_ROLES,
  DAILY_PRODUCTION_PROCESSES,
  DailyProductionProcess,
} from '@app/models/production/daily-production-process';
import {
  PRODUCTION_DETAIL_PROCESS_ROLES,
  PRODUCTION_DETAIL_PROCESSES,
  ProductionDetailProcess,
} from '@app/models/production/production-detail-process';
import { ProductionReportsRoles } from '@app/models/roles/roles-group';
import { ReportProxyService } from '@app/proxies/production/report.proxy.service';
import { ShiftProxyService } from '@app/proxies/production/shift.proxy.service';
import { LocaleHelperService } from '@app/services/locale-helper.service';
import { formatFileNameTimestamp, getMonthOptions, getYearOptions } from '@app/utilities/date.util';

@Component({
  selector: 'app-production-reporting',
  templateUrl: './production-reporting.component.html',
  styleUrl: './production-reporting.component.scss',
})
export class ProductionReportingComponent {
  private readonly reportProxyService = inject(ReportProxyService);
  private readonly shiftProxyService = inject(ShiftProxyService);
  private readonly localeHelperService = inject(LocaleHelperService);

  loadingKnittingReport = signal(0);
  loadingChemicalConsumption = signal(0);
  loadingMachineAssignment = signal(0);
  loadingDailyProduction = signal(0);
  loadingProductionDetail = signal(0);

  isAnyReportLoading = computed(
    () =>
      this.loadingKnittingReport() > 0 ||
      this.loadingChemicalConsumption() > 0 ||
      this.loadingMachineAssignment() > 0 ||
      this.loadingDailyProduction() > 0 ||
      this.loadingProductionDetail() > 0,
  );

  private updateLoading(loading: WritableSignal<number>, delta: number): void {
    loading.update((value) => Math.max(value + delta, 0));
  }

  knittingForm = new FormGroup({
    fromDate: new FormControl<Date | null>(this.startOfDay(), [Validators.required]),
    toDate: new FormControl<Date | null>(this.endOfDay(), [Validators.required]),
  });

  private startOfDay(): Date {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }

  private endOfDay(): Date {
    const d = new Date();
    d.setHours(23, 59, 0, 0);
    return d;
  }

  chemicalConsumptionForm = new FormGroup({
    year: new FormControl<number | null>(null, [Validators.required]),
    month: new FormControl<number | null>(null, [Validators.required]),
    includeAllConsumptions: new FormControl<boolean>(false, { nonNullable: true }),
  });

  machineAssignmentForm = new FormGroup({
    year: new FormControl<number | null>(null, [Validators.required]),
    month: new FormControl<number | null>(null, [Validators.required]),
  });

  dailyProductionForm = new FormGroup({
    fromDate: new FormControl<Date | null>(this.startOfDay(), [Validators.required]),
    toDate: new FormControl<Date | null>(this.endOfDay(), [Validators.required]),
    processes: new FormControl<string[]>([]),
  });

  productionDetailForm = new FormGroup({
    fromDate: new FormControl<Date | null>(this.startOfDay(), [Validators.required]),
    toDate: new FormControl<Date | null>(this.endOfDay(), [Validators.required]),
    processes: new FormControl<string[]>([]),
  });

  get allowedDailyProductionProcesses(): DailyProductionProcess[] {
    return DAILY_PRODUCTION_PROCESSES.filter((process) => AuthService.hasRole(DAILY_PRODUCTION_PROCESS_ROLES[process]));
  }

  get dailyProductionProcessOptions() {
    return this.allowedDailyProductionProcesses.map((process) => ({
      label: this.localeHelperService.translateWithFallback(`ProductionReporting.process_${process}`, process),
      value: process,
    }));
  }

  get canViewDailyProduction(): boolean {
    return this.allowedDailyProductionProcesses.length > 0;
  }

  get canViewGeneralReports(): boolean {
    return AuthService.hasAnyRole([ProductionReportsRoles.ProductionReportsViewer, ProductionReportsRoles.ProductionReportsAdmin]);
  }

  get allowedProductionDetailProcesses(): ProductionDetailProcess[] {
    return PRODUCTION_DETAIL_PROCESSES.filter((process) => AuthService.hasRole(PRODUCTION_DETAIL_PROCESS_ROLES[process]));
  }

  get productionDetailProcessOptions() {
    return this.allowedProductionDetailProcesses.map((process) => ({
      label: this.localeHelperService.translateWithFallback(`ProductionReporting.process_${process}`, process),
      value: process,
    }));
  }

  get canViewProductionDetail(): boolean {
    return this.allowedProductionDetailProcesses.length > 0;
  }

  get monthOptions() {
    return getMonthOptions(this.localeHelperService);
  }

  readonly yearOptions = getYearOptions();

  downloadKnittingReport(): void {
    if (!this.knittingForm.valid) {
      this.knittingForm.markAllAsTouched();
      GlobalMessageUtil.warn(
        this.localeHelperService.translateWithFallback('CommonMessages.Warning', 'Warning'),
        this.localeHelperService.translateWithFallback('ProductionReporting.fill_required_fields', 'Please fill in all required fields'),
      );
      return;
    }

    const { fromDate, toDate } = this.knittingForm.value;
    const offsetMinutes = new Date().getTimezoneOffset();

    const from = new Date(fromDate!);
    from.setSeconds(0, 0);
    const to = new Date(toDate!);
    to.setSeconds(59, 0);

    this.updateLoading(this.loadingKnittingReport, 1);

    this.shiftProxyService
      .downloadShiftReport({ fromDate: from.toISOString(), toDate: to.toISOString() }, offsetMinutes)
      .pipe(finalize(() => this.updateLoading(this.loadingKnittingReport, -1)))
      .subscribe({
        next: (blob) => {
          const dateStr = from.toISOString().split('T')[0];
          this.downloadBlob(blob, `Knitting-Production-Report_${dateStr}.pdf`);
          GlobalMessageUtil.success(
            this.localeHelperService.translateWithFallback('CommonMessages.Success', 'Success'),
            this.localeHelperService.translateWithFallback(
              'ProductionReporting.knitting_report_generated',
              'Knitting Production report generated successfully',
            ),
          );
        },
      });
  }

  downloadChemicalConsumptionReport(): void {
    if (!this.chemicalConsumptionForm.valid) {
      this.chemicalConsumptionForm.markAllAsTouched();
      GlobalMessageUtil.warn(
        this.localeHelperService.translateWithFallback('CommonMessages.Warning', 'Warning'),
        this.localeHelperService.translateWithFallback('ProductionReporting.fill_required_fields', 'Please fill in all required fields'),
      );
      return;
    }

    const { year, month, includeAllConsumptions } = this.chemicalConsumptionForm.getRawValue();
    // The API adds this offset to UTC, so it needs the ISO sign (UTC-6 => -360),
    // the inverse of what getTimezoneOffset() returns.
    const offsetMinutes = -new Date().getTimezoneOffset();

    this.updateLoading(this.loadingChemicalConsumption, 1);

    this.reportProxyService
      .getChemicalConsumptionReport(year!, month!, offsetMinutes, includeAllConsumptions)
      .pipe(finalize(() => this.updateLoading(this.loadingChemicalConsumption, -1)))
      .subscribe({
        next: (blob) => {
          const monthStr = month!.toString().padStart(2, '0');
          this.downloadBlob(blob, `Chemical-Consumption_${year}-${monthStr}.xlsx`);
          GlobalMessageUtil.success(
            this.localeHelperService.translateWithFallback('CommonMessages.Success', 'Success'),
            this.localeHelperService.translateWithFallback(
              'ProductionReporting.chemical_consumption_generated',
              'Chemical Consumption report generated successfully',
            ),
          );
        },
      });
  }

  downloadMachineAssignmentReport(): void {
    if (!this.machineAssignmentForm.valid) {
      this.machineAssignmentForm.markAllAsTouched();
      GlobalMessageUtil.warn(
        this.localeHelperService.translateWithFallback('CommonMessages.Warning', 'Warning'),
        this.localeHelperService.translateWithFallback('ProductionReporting.fill_required_fields', 'Please fill in all required fields'),
      );
      return;
    }

    const { year, month } = this.machineAssignmentForm.value;
    // The API adds this offset to UTC, so it needs the ISO sign (UTC-6 => -360),
    // the inverse of what getTimezoneOffset() returns.
    const offsetMinutes = -new Date().getTimezoneOffset();

    this.updateLoading(this.loadingMachineAssignment, 1);

    this.reportProxyService
      .getMachineAssignmentReport(year!, month!, offsetMinutes)
      .pipe(finalize(() => this.updateLoading(this.loadingMachineAssignment, -1)))
      .subscribe({
        next: (blob) => {
          const monthStr = month!.toString().padStart(2, '0');
          this.downloadBlob(blob, `Machine-Assignment_${year}-${monthStr}.xlsx`);
          GlobalMessageUtil.success(
            this.localeHelperService.translateWithFallback('CommonMessages.Success', 'Success'),
            this.localeHelperService.translateWithFallback(
              'ProductionReporting.machine_assignment_generated',
              'Machine Assignment report generated successfully',
            ),
          );
        },
      });
  }

  downloadDailyProductionReport(): void {
    if (!this.dailyProductionForm.valid) {
      this.dailyProductionForm.markAllAsTouched();
      GlobalMessageUtil.warn(
        this.localeHelperService.translateWithFallback('CommonMessages.Warning', 'Warning'),
        this.localeHelperService.translateWithFallback('ProductionReporting.fill_required_fields', 'Please fill in all required fields'),
      );
      return;
    }

    const { fromDate, toDate, processes } = this.dailyProductionForm.value;
    // The API adds this offset to UTC, so it needs the ISO sign (UTC-6 => -360),
    // the inverse of what getTimezoneOffset() returns.
    const offsetMinutes = -new Date().getTimezoneOffset();
    // An empty `processes` means "all" to the API, which would ignore the user's roles.
    // Send the allowed set explicitly instead of relying on that default.
    const selectedProcesses = processes?.length ? processes : this.allowedDailyProductionProcesses;

    const from = new Date(fromDate!);
    from.setSeconds(0, 0);
    const to = new Date(toDate!);
    to.setSeconds(59, 0);

    this.updateLoading(this.loadingDailyProduction, 1);

    this.reportProxyService
      .getDailyProductionReport(from.toISOString(), to.toISOString(), offsetMinutes, selectedProcesses)
      .pipe(finalize(() => this.updateLoading(this.loadingDailyProduction, -1)))
      .subscribe({
        next: (blob) => {
          const fromStr = from.toISOString().split('T')[0];
          const toStr = to.toISOString().split('T')[0];
          // Name the file after the process when exactly one was picked, so downloading
          // several one at a time does not produce indistinguishable files.
          const scope = selectedProcesses.length === 1 ? `${selectedProcesses[0]}_` : '';
          this.downloadBlob(blob, `Daily-Production_${scope}${fromStr}_${toStr}_${formatFileNameTimestamp()}.xlsx`);
          GlobalMessageUtil.success(
            this.localeHelperService.translateWithFallback('CommonMessages.Success', 'Success'),
            this.localeHelperService.translateWithFallback(
              'ProductionReporting.daily_production_generated',
              'Daily Production report generated successfully',
            ),
          );
        },
      });
  }

  downloadProductionDetailReport(): void {
    if (!this.productionDetailForm.valid) {
      this.productionDetailForm.markAllAsTouched();
      GlobalMessageUtil.warn(
        this.localeHelperService.translateWithFallback('CommonMessages.Warning', 'Warning'),
        this.localeHelperService.translateWithFallback('ProductionReporting.fill_required_fields', 'Please fill in all required fields'),
      );
      return;
    }

    const { fromDate, toDate, processes } = this.productionDetailForm.value;
    // The API adds this offset to UTC, so it needs the ISO sign (UTC-6 => -360),
    // the inverse of what getTimezoneOffset() returns.
    const offsetMinutes = -new Date().getTimezoneOffset();
    // An empty `processes` means "all" to the API, which would ignore the user's roles.
    // Send the allowed set explicitly instead of relying on that default.
    const selectedProcesses = processes?.length ? processes : this.allowedProductionDetailProcesses;

    const from = new Date(fromDate!);
    from.setSeconds(0, 0);
    const to = new Date(toDate!);
    to.setSeconds(59, 0);

    this.updateLoading(this.loadingProductionDetail, 1);

    this.reportProxyService
      .getProductionDetailReport(from.toISOString(), to.toISOString(), offsetMinutes, selectedProcesses)
      .pipe(finalize(() => this.updateLoading(this.loadingProductionDetail, -1)))
      .subscribe({
        next: (blob) => {
          const fromStr = from.toISOString().split('T')[0];
          const toStr = to.toISOString().split('T')[0];
          // Name the file after the area when exactly one was picked, so downloading
          // several one at a time does not produce indistinguishable files.
          const scope = selectedProcesses.length === 1 ? `${selectedProcesses[0]}_` : '';
          this.downloadBlob(blob, `Area-Production-Detail_${scope}${fromStr}_${toStr}_${formatFileNameTimestamp()}.xlsx`);
          GlobalMessageUtil.success(
            this.localeHelperService.translateWithFallback('CommonMessages.Success', 'Success'),
            this.localeHelperService.translateWithFallback(
              'ProductionReporting.production_detail_generated',
              'Area Production Detail report generated successfully',
            ),
          );
        },
      });
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}
